import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'

interface Podcast {
    _id: string;
    title: string;
    description: string;
    videoUrl: string;
    thumbnail: string;
    links: { label: string; url: string }[];
}

export default function AdminManagePodcasts() {
    const { token } = useAuth()
    const [podcasts, setPodcasts] = useState<Podcast[]>([])
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [showForm, setShowForm] = useState(false)

    const [form, setForm] = useState({
        title: '',
        description: '',
        videoUrl: '',
        thumbnail: '',
        links: [{ label: '', url: '' }]
    })

    const extractYouTubeId = (url: string) => {
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : null;
    };

    const handleVideoUrlChange = (url: string) => {
        const videoId = extractYouTubeId(url);
        let thumbnailUrl = form.thumbnail;
        
        if (videoId) {
            thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }

        setForm({
            ...form,
            videoUrl: url,
            thumbnail: thumbnailUrl
        });
    };

    useEffect(() => {
        fetchPodcasts()
    }, [])

    const fetchPodcasts = async () => {
        try {
            const res = await fetch(`${API_BASE}/podcasts`)
            const data = await res.json()
            if (Array.isArray(data)) setPodcasts(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleAddLink = () => {
        setForm({ ...form, links: [...form.links, { label: '', url: '' }] })
    }

    const handleLinkChange = (index: number, field: 'label' | 'url', value: string) => {
        const newLinks = [...form.links]
        newLinks[index][field] = value
        setForm({ ...form, links: newLinks })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSubmitting(true)
        setMsg(null)

        try {
            const res = await fetch(`${API_BASE}/podcasts${editingId ? `/${editingId}` : ''}`, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(form)
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editingId ? 'Podcast updated successfully!' : 'Podcast added successfully!' })
                setForm({ title: '', description: '', videoUrl: '', thumbnail: '', links: [{ label: '', url: '' }] })
                setEditingId(null)
                setShowForm(false)
                fetchPodcasts()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || `Failed to ${editingId ? 'update' : 'add'} podcast` })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this podcast?')) return
        try {
            const res = await fetch(`${API_BASE}/podcasts/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setPodcasts(prev => prev.filter(p => p._id !== id))
                setMsg({ type: 'success', text: 'Podcast deleted.' })
                if (editingId === id) {
                    setEditingId(null)
                    setForm({ title: '', description: '', videoUrl: '', thumbnail: '', links: [{ label: '', url: '' }] })
                }
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (p: Podcast) => {
        setEditingId(p._id)
        setForm({
            title: p.title,
            description: p.description || '',
            videoUrl: p.videoUrl,
            thumbnail: p.thumbnail,
            links: p.links && p.links.length > 0 ? p.links : [{ label: '', url: '' }]
        })
        setShowForm(true)
    }

    const cancelEdit = () => {
        setEditingId(null)
        setForm({ title: '', description: '', videoUrl: '', thumbnail: '', links: [{ label: '', url: '' }] })
        setShowForm(false)
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h1 className="text-3xl font-bold text-black mb-2 tracking-tight">Podcast Library</h1>
                    <p className="text-black text-sm font-bold">Curate and broadcast high-impact audio/video episodes to your community.</p>
                </div>
                <button 
                    onClick={() => { setEditingId(null); setForm({ title: '', description: '', videoUrl: '', thumbnail: '', links: [{ label: '', url: '' }] }); setShowForm(true); }}
                    className="px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[14px] transition-all shadow-md active:scale-95 flex items-center gap-3"
                >
                    <i className="fa-solid fa-plus text-[12px]"></i> Release New Podcast
                </button>
            </div>

                {/* Notification Area */}
                {msg && (
                    <div className={`mb-10 p-5 rounded-md flex items-center gap-4 animate-in fade-in slide-in-from-top-4 border shadow-sm ${
                        msg.type === 'error' 
                        ? 'bg-rose-50 text-rose-700 border-rose-100' 
                        : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}>
                        <i className={`fa-solid ${msg.type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-check'} text-lg`}></i>
                        <p className="text-[14px] font-bold">{msg.text}</p>
                    </div>
                )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden mb-12">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] flex items-center justify-center shadow-xl shadow-blue-100">
                            <i className="fa-solid fa-microphone-lines text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-black tracking-tight">Active Catalog</h3>
                            <p className="text-[12px] font-bold text-black/40 uppercase tracking-widest">{podcasts.length} Broadcast Episodes</p>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="w-16 h-16 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            <p className="text-black font-bold text-[14px]">Syncing Frequency...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-blue-900/10 dark:border-slate-800">
                                    <th className="px-8 py-5 text-[12px] font-bold text-black uppercase">Episode Media</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-black uppercase text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {podcasts.map(p => (
                                    <tr key={p._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-8">
                                                <div className="relative flex-shrink-0 group/thumb">
                                                    <div className="absolute inset-0 bg-blue-950 rounded-md scale-[1.05] opacity-0 group-hover/thumb:opacity-20 transition-all duration-500"></div>
                                                    <img src={getImageUrl(p.thumbnail || 'https://via.placeholder.com/300')} alt={p.title} className="w-40 aspect-video rounded-md object-cover shadow-xl group-hover/thumb:scale-[1.02] transition-transform duration-500 ring-4 ring-white dark:ring-slate-800" />
                                                    <div className="absolute inset-0 bg-blue-950/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity rounded-md flex items-center justify-center backdrop-blur-[2px]">
                                                        <i className="fa-solid fa-play text-white text-2xl"></i>
                                                    </div>
                                                </div>
                                                <div className="max-w-xl">
                                                    <h4 className="font-bold text-black group-hover:text-black/60 transition-colors text-[17px] tracking-tight leading-tight">{p.title}</h4>
                                                    <p className="text-[13px] text-black/60 mt-2 line-clamp-2 font-bold leading-relaxed">
                                                        {p.description || "No transmission log provided for this broadcast session."}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2 mt-4">
                                                        {p.links?.map((l, i) => (
                                                            l.label && l.url && (
                                                                <span key={i} className="px-3 py-1 rounded-md bg-gray-50 text-[9px] font-bold uppercase text-black border border-gray-100 tracking-widest">{l.label}</span>
                                                            )
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="flex items-center justify-end gap-3 transition-all">
                                                    <button 
                                                        className="w-11 h-11 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-sm font-bold text-[12px]"
                                                        onClick={() => handleEdit(p)}
                                                        title="Modify broadcast"
                                                    >
                                                        <i className="fa-solid fa-pen-nib text-xs"></i>
                                                    </button>
                                                    <button 
                                                        className="w-11 h-11 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-rose-100 font-bold text-[12px]"
                                                        onClick={() => handleDelete(p._id)}
                                                        title="Purge sequence"
                                                    >
                                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                                    </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {podcasts.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="px-8 py-32 text-center text-balance overflow-hidden">
                                            <div className="w-24 h-24 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-8 shadow-inner border border-gray-100">
                                                <i className="fa-solid fa-signal-stream-slash text-4xl text-gray-200"></i>
                                            </div>
                                            <h3 className="text-xl font-bold text-black mb-2 tracking-tight">Frequency Silent</h3>
                                            <p className="text-black text-sm max-w-xs mx-auto font-bold">Upload your first broadcast episode to initialize the media library.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Premium Dialog Overlay */}
            {showForm && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-blue-950/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={cancelEdit}></div>
                    <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300">
                            <div className="relative h-28 bg-[#05144b] flex flex-col justify-end p-6">
                                <button 
                                    onClick={cancelEdit}
                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                                >
                                    <i className="fa-solid fa-xmark text-sm"></i>
                                </button>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-md bg-white p-1 shadow-2xl">
                                        <div className="w-full h-full rounded-[4px] bg-white flex items-center justify-center overflow-hidden">
                                            {form.thumbnail ? (
                                                <img src={getImageUrl(form.thumbnail)} alt="Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <i className="fa-solid fa-microphone-lines text-xl text-[#05144b]"></i>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-1">
                                        <h2 className="text-lg font-bold text-white tracking-tight">{editingId ? 'Modify Broadcast' : 'New Intake Session'}</h2>
                                        <p className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Broadcast Config</p>
                                    </div>
                                </div>
                            </div>

                        <form onSubmit={handleSubmit} className="p-6 px-8">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Episode Narrative</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Episode Title"
                                                value={form.title} 
                                                onChange={e => setForm({ ...form, title: e.target.value })} 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Broadcast Synopsis</label>
                                            <textarea 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] min-h-[100px] shadow-sm leading-relaxed" 
                                                placeholder="Brief Synopsis..."
                                                value={form.description} 
                                                onChange={e => setForm({ ...form, description: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">YouTube Source</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                    <i className="fa-brands fa-youtube text-rose-500"></i>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 pl-10 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                    placeholder="Paste YouTube Link..."
                                                    value={form.videoUrl} 
                                                    onChange={e => handleVideoUrlChange(e.target.value)} 
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Visual Identification</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Auto-captured"
                                                value={form.thumbnail} 
                                                onChange={e => setForm({ ...form, thumbnail: e.target.value })} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-blue-900/10 dark:border-slate-800">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-[11px] font-bold text-black uppercase">Auxiliary Channels</label>
                                        <button 
                                            type="button" 
                                            onClick={handleAddLink}
                                            className="text-[11px] font-bold uppercase text-black hover:text-black/60 transition-colors flex items-center gap-1"
                                        >
                                            <i className="fa-solid fa-plus text-[8px]"></i> Add Link
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {form.links.map((link, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                <input 
                                                    type="text" 
                                                    className="flex-[0.4] px-4 py-2.5 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all text-[12px] font-bold shadow-sm" 
                                                    placeholder="Channel" 
                                                    value={link.label} 
                                                    onChange={e => handleLinkChange(idx, 'label', e.target.value)} 
                                                />
                                                <input 
                                                    type="text" 
                                                    className="flex-1 px-4 py-2.5 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all text-[12px] font-bold shadow-sm" 
                                                    placeholder="URL" 
                                                    value={link.url} 
                                                    onChange={e => handleLinkChange(idx, 'url', e.target.value)} 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button"
                                    onClick={cancelEdit}
                                    className="px-6 py-3 rounded-md bg-white text-black font-bold text-[12px] hover:bg-gray-50 transition-all border border-gray-100"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className={`px-8 py-3 rounded-md font-bold text-[14px] transition-all transform active:scale-95 shadow-md flex items-center justify-center gap-2 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50`}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <i className={`fa-solid ${editingId ? 'fa-floppy-disk' : 'fa-paper-plane'} text-[12px]`}></i>
                                            <span>{editingId ? 'Update Broadcast' : 'Release Podcast'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


